import { computed, Signal, signal } from '@angular/core';
import { DataDisplayOptions, DataFormat, DataSortMode, SchemaFieldTargetDto, SortDirection } from 'src/app/services/api.services';
import { RichTextValue } from '../RichTextValue';
import { VisualFieldRole } from './VisualFieldRole';
import { VisualFieldTarget } from './VisualFieldTarget';
import { DataAggregation } from '../dataModel';
import { v7 as uuid7 } from 'uuid';

let nextFieldId = 1;

export class VisualField {
  /** Gets a unique ID of this field, in the local application's context. */
  readonly id = nextFieldId++;

  get referenceString() { return this.target.referenceString; }

  constructor(
    public readonly target: VisualFieldTarget,
    public readonly visualFieldId?: string,
    sortingInit?: VisualFieldSortingInit
    ) {
    this.visualFieldId ??= uuid7();

    if (!!sortingInit) {
      this.sorted = signal(sortingInit.sorted);
      this.sortDirection = signal(sortingInit.sortDirection);
      this.sortOrder = signal(sortingInit.sortOrder);
    }
  }


  /**
   * Clones this field.
   * Note - the clone will not have a role assigned, even if this field does. */
  clone(newTarget?: VisualFieldTarget): VisualField {
    let clone = new VisualField(newTarget ?? this.target, this.visualFieldId);

    clone.sorted.set(this.sorted());
    clone.sortOrder.set(this.sortOrder());
    clone.sortDirection.set(this.sortDirection());
    clone.customDisplayName.set(this.customDisplayName());
    clone.hidden.set(this.hidden());
    clone.customDataFormat.set(this.customDataFormat());
    clone.customDataDisplay.set(this.customDataDisplay());

    return clone;
  }


  /**
   * Note - This property should not be directly assigned.
   * Role assignment is managed by {@link VisualFieldCollection}.
   * Gets the role of this field in a visual.
   * When null, the field has not been assigned a role. */
  readonly role: Signal<VisualFieldRole> = signal<VisualFieldRole>(null);
  readonly roleIndex: Signal<number> = signal<number>(null);


  //#region Sorting
  /** Gets or sets whether this field should be sorted. */
  readonly sorted = signal<boolean>(false);

  /**
   * Gets or sets the order of sorting for this field.
   * This order is ascending, i.e. fields should be sorted in order from lowest to highest number. 
   * When null, the field should be sorted after all fields with a sort order, following display order. */
  readonly sortOrder = signal<number | null>(null);

  /**
   * Gets or sets the direction of sorting for this field.
   * When null, the field should use default sorting direction. */
  readonly sortDirection = signal<SortDirection | null>(null);

  /**
   * Gets default sort direction for this field.
   * This will be its schema item's default sort when assigned.
   * Otherwise it will be the default sort for its data type. */
  readonly defaultSortDirection = computed(() => this.target.defaultSortDirection);

  readonly assignedOrDefaultSortDirection = computed(() => this.sorted() ? this.sortDirection() : this.defaultSortDirection());
  //#endregion


  //#region Display
  /**
   * Gets or sets a custom display name for this field.
   * The custom display name takes precedence over the field's original display name.
   * The custom display name does not affect references to this field (e.g. identifier expressions). */
  readonly customDisplayName = signal<string | null>(null);

  /**
   * Gets or sets whether this field should be hidden.
   * When hidden, the field should not appear on a visual.
   * The field should still take part in the query, regardless of whether it is hidden. */
  readonly hidden = signal<boolean>(false);


  /**
   * Gets the icon of this field.
   * This generally is editor specific icon displayed in the field selector. */
  get icon(): string { return this.target.icon; }

  /**
   * Gets the default title of this field.
   * This is either {@link customDisplayName}'s value when assigned, or the field's original title. */
  get shortTitle(): string { return this.customDisplayName() ?? this.target.shortTitle; }

  /**
   * Gets the default long title of this field.
   * Default long title is separated using the ' - ' string. */
  get longTitle(): string { return this.target.longTitle; }

  /** Gets the content for a description of this field. */
  get description(): RichTextValue { return this.target.description; };

  /** Gets the content for a tooltip of this field. */
  get tooltip(): RichTextValue { return this.target.tooltip; };

  /** Formats long title of this field with the given separator. */
  formatLongTitle(separator?: string): string {
    return this.target.formatLongTitle(separator);
  }
  //#endregion


  //#region Source
  /**
   * Gets the schema item this field's source is associated with.
   * If the target is not associated with a schema item, returns null. */
  get schemaItem() { return this.target.schemaItem; }

  /**
   * Gets the custom field this field's source is associated with.
   * If the target is not associated with a custom field, returns null. */
  get customField() { return this.target.customField; }


  /** Gets the schema this field's source is associated with. */
  get schema() { return this.target.schema; }


  /**
   * Indicates whether the field's source is valid.
   * A field may be invalid if it references a missing schema item
   * or its custom field has errors in its expression. */
  readonly isValid = computed(() => this.target.isValid);

  readonly isInvalidCalculatedField = computed(() => this.customField && !this.isValid());

  readonly error = computed(() => this.target.getErrorText());


  get unaggregatedDataType() { return this.target.unaggregatedDataType; }

  /** Gets the data type for this field's source. */
  get dataType() { return this.target.dataType; }


  /**
   * Gets or sets a custom data format for this field.
   * The custom data format takes precedence over the field's original data format. */
  readonly customDataFormat = signal<DataFormat | null>(null);

  /**
   * Gets the effective data format for this field.
   * This is either customDataFormat, when assigned - or the source's data format. */
  get dataFormat() { return this.customDataFormat() ?? this.target.dataFormat; }

  /**
   * Gets or sets a custom data display for this field.
   * The custom data display takes precedence over the field's original data display. */
  readonly customDataDisplay = signal<DataDisplayOptions | null>(null);

  /**
   * Gets the effective data display for this field.
   * This is either customDataDisplay, when assigned - or the source's data display. */
  get dataDisplay() { return this.customDataDisplay() ?? this.target.dataDisplay; }


  /**
   * Gets the aggregation function applied to this field's source.
   * If no aggregation function has been applied, returns null. */
  get aggregation() { return this.target.aggregation; }

  /**
   * Indicates whether the field is aggregated.
   * A field is aggregated if its source is aggregated, or has an explicit aggregation assigned. */
  get isAggregated() { return this.target.isAggregated; }


  /**
   * Whether this field's aggregation can be changed.
   * A custom field that aggregates cannot change its aggregation. */
  get canAssignAggregation() { return this.target.canAssignAggregation; }

  /**
   * Aggregation display text, e.g. `Sum of X` */
  get measureTitle() { return this.target.measureTitle; }


  /** Returns `aggregation`, and when not asssigned - `dataType.defaultAggregation`. */
  getOwnOrDefaultAggregation() {
    return this.aggregation ?? this.unaggregatedDataType.defaultAggregation;
  }

  /**
   * Indicates whether the field has currency variants.
   * A field with currency variants would use those variants
   * when specific currency is selected. */
  get hasCurrencyVariants() { return this.target.hasCurrencyVariants; }

  /**
   * Indicates whether the field is a user defined field.
   * A UDF is usually a source system defined property, not driven by Insights itself. */
  get isUDF() { return !!this.target.schemaItem?.field.isUDF; }



  /**
   * Gets the expression that can be used to reference this field
   * in an InsightsQuery or DataQuery expression code. */
  get identifierExpr() { return this.target.identifierExpr; }

  /**
   * Gets a {@link SchemaFieldTargetDto} object that represents this field's target.
   * The result includes {@link customDisplayName} when assigned.
   */
  get schemaFieldTargetDto(): SchemaFieldTargetDto {
    return {
      ...this.target.schemaFieldTargetDto,
      displayName: this.customDisplayName()
    };
  }
  //#endregion
}

export interface VisualFieldSortingInit {
  sorted?: boolean,
  sortDirection?: SortDirection,
  sortOrder?: number;
}
